// hooks/useVideoNavigation.ts
// Custom hook for video navigation state and titles

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { videos } from '../videoConfig'
import { ACTION_TYPES, ACCOUNT_TYPES, VIDEO_CONFIG } from '../constants'

interface UseVideoNavigationProps {
  currentIndex: number
  selectedAvatar: string | null
  selectedLanguage: string | null
  selectedVariantIntro: string | null
}

export function useVideoNavigation({
  currentIndex,
  selectedAvatar,
  selectedLanguage,
  selectedVariantIntro
}: UseVideoNavigationProps) {
  const { t } = useTranslation('videos')

  // Compute navigation availability
  const hasPrev = currentIndex > 0

  const hasNext = useMemo(() => {
    return (
      currentIndex < videos.length - VIDEO_CONFIG.END_VIDEOS_COUNT &&
      videos[currentIndex].action?.type !== ACTION_TYPES.SWITCH_INTRO &&
      selectedLanguage !== null
    )
  }, [currentIndex, selectedLanguage])

  const hasPrevCategory = useMemo(() => {
    return videos.slice(0, currentIndex).some(v => v.isCategoryVideo)
  }, [currentIndex])

  const hasNextCategory = useMemo(() => {
    if (videos[currentIndex].action?.type === ACTION_TYPES.SWITCH_INTRO) {
      return false
    }

    const nextCategoryVideo = videos.slice(currentIndex + 1).find(v => v.isCategoryVideo)
    if (!nextCategoryVideo) return false

    const excludedTypes = [
      ACTION_TYPES.QA,
      ACTION_TYPES.CONTACT_FORM,
      ACTION_TYPES.END,
      ACTION_TYPES.SWITCH
    ]

    const actionType = nextCategoryVideo.action?.type
    return !actionType || !excludedTypes.includes(actionType as any)
  }, [currentIndex])

  // Compute titles for navigation buttons
  const prevTitle = useMemo(() => {
    const video = videos[currentIndex - 1]
    if (!video?.titleKey) return ""
    return t(`titles.${video.titleKey}`)
  }, [currentIndex, t])

  const nextTitle = useMemo(() => {
    const skipCount =
      videos[currentIndex].action?.type === ACTION_TYPES.FEES &&
      selectedVariantIntro === ACCOUNT_TYPES.MAX
        ? VIDEO_CONFIG.MAX_ACCOUNT_SKIP_COUNT
        : 1

    const video = videos[currentIndex + skipCount]
    if (!video?.titleKey) return ""
    return t(`titles.${video.titleKey}`)
  }, [currentIndex, selectedVariantIntro, t])

  const prevCategoryTitle = useMemo(() => {
    const prevVideos = videos.slice(0, currentIndex)
    const prevCategoryVideo = [...prevVideos].reverse().find(v => v.isCategoryVideo)
    if (!prevCategoryVideo?.titleKey) return ""
    return t(`titles.${prevCategoryVideo.titleKey}`)
  }, [currentIndex, t])

  const nextCategoryTitle = useMemo(() => {
    const nextVideos = videos.slice(currentIndex + 1)
    const nextCategoryVideo = nextVideos.find(v => v.isCategoryVideo)
    if (!nextCategoryVideo?.titleKey) return ""
    return t(`titles.${nextCategoryVideo.titleKey}`)
  }, [currentIndex, t])

  return {
    hasPrev,
    hasNext,
    hasPrevCategory,
    hasNextCategory,
    prevTitle,
    nextTitle,
    prevCategoryTitle,
    nextCategoryTitle
  }
}
